import { useMemo, useState } from "react";
import { Download } from "lucide-react";
import { Modal } from "../ui/Modal";
import type { DataSheetColumn, DataSheetRecord } from "../../api/dataSheetsApi";
import {
  buildExportRows,
  downloadExport,
  isReferenceType,
  type ExportFormat,
  type RefMode,
} from "../../lib/dataSheetTransfer";

/**
 * Export dialog for a data sheet. Lets the user pick CSV or Excel and,
 * per reference-typed column, whether to write the human *value* (name)
 * or the stored *reference* (id). Scalar columns export as stored.
 */
export function DataSheetExportModal({
  open,
  onClose,
  sheetName,
  columns,
  records,
}: {
  open: boolean;
  onClose: () => void;
  sheetName: string;
  columns: DataSheetColumn[];
  records: DataSheetRecord[];
}) {
  const [format, setFormat] = useState<ExportFormat>("xlsx");
  const [modes, setModes] = useState<Record<number, RefMode>>({});

  const refColumns = useMemo(() => columns.filter((c) => isReferenceType(c.typeId)), [columns]);

  const run = () => {
    const { headers, rows } = buildExportRows(columns, records, modes);
    downloadExport(format, sheetName || "data-sheet", headers, rows);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Export data sheet"
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={run}
            disabled={records.length === 0}
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <Download size={16} /> Export {records.length} {records.length === 1 ? "row" : "rows"}
          </button>
        </>
      }
    >
      <div style={{ display: "grid", gap: 16 }}>
        <div style={{ display: "grid", gap: 6 }}>
          <span style={{ fontSize: "0.9rem", fontWeight: 700 }}>Format</span>
          <div style={{ display: "flex", gap: 16 }}>
            {(["xlsx", "csv"] as ExportFormat[]).map((f) => (
              <label key={f} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <input type="radio" name="export-format" checked={format === f} onChange={() => setFormat(f)} />
                {f === "xlsx" ? "Excel (.xlsx)" : "CSV (.csv)"}
              </label>
            ))}
          </div>
        </div>

        {refColumns.length > 0 && (
          <div style={{ display: "grid", gap: 8 }}>
            <span style={{ fontSize: "0.9rem", fontWeight: 700 }}>Reference columns</span>
            <p style={{ margin: 0, fontSize: "0.85rem", color: "var(--portal-muted)" }}>
              <strong>Value</strong> writes the name (e.g. "Support agents"); <strong>Reference</strong> writes
              the id so the file can be re-imported without name matching.
            </p>
            {refColumns.map((c) => {
              const mode = modes[c.slot] ?? "value";
              return (
                <div
                  key={c.slot}
                  style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0", borderTop: "1px solid var(--portal-border)" }}
                >
                  <span style={{ flex: 1, fontWeight: 600 }}>{c.name}</span>
                  {(["value", "reference"] as RefMode[]).map((m) => (
                    <label key={m} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: "0.9rem" }}>
                      <input
                        type="radio"
                        name={`ref-mode-${c.slot}`}
                        checked={mode === m}
                        onChange={() => setModes({ ...modes, [c.slot]: m })}
                      />
                      {m === "value" ? "Value" : "Reference"}
                    </label>
                  ))}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Modal>
  );
}
